import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Check, ChevronsUpDown } from 'lucide-react';
import { 
  FileText, Download, Plus, Eye, Send, CheckCircle, 
  Calendar, Building2, DollarSign, Trash2, Search,
  AlertCircle, Receipt, CreditCard, Mail, Printer,
  FileSpreadsheet, ArrowRight, FileCheck2, Banknote,
  Clock, RefreshCw, ChevronLeft, ChevronRight, Bell,
  ChevronsLeft, ChevronsRight, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  paid: 'bg-emerald-100 text-emerald-700',
  overdue: 'bg-red-100 text-red-700',
  partial: 'bg-amber-100 text-amber-700',
  converted: 'bg-purple-100 text-purple-700'
};

const PAYMENT_STATUS_COLORS = {
  unpaid: 'bg-red-100 text-red-700',
  partial: 'bg-amber-100 text-amber-700',
  paid: 'bg-emerald-100 text-emerald-700'
};

// Date presets
const getDatePresets = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  return {
    this_month: {
      from: new Date(y, m, 1).toISOString().split('T')[0],
      to: new Date(y, m + 1, 0).toISOString().split('T')[0]
    },
    last_month: {
      from: new Date(y, m - 1, 1).toISOString().split('T')[0],
      to: new Date(y, m, 0).toISOString().split('T')[0]
    },
    last_3_months: {
      from: new Date(y, m - 3, 1).toISOString().split('T')[0],
      to: new Date(y, m + 1, 0).toISOString().split('T')[0]
    }
  };
};

export default function ClientInvoices({ isClientView = false, clientId = null }) {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [invoiceType, setInvoiceType] = useState('invoice');
  
  // Filters
  const [filterClient, setFilterClient] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');

  // Pagination
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  
  // Generate form — entity_id is mandatory per-entity estimate per user direction
  const [generateForm, setGenerateForm] = useState({
    client_id: '',
    entity_id: '',
    period_from: '',
    period_to: '',
    tax_rate: 18,
    notes: '',
    due_date: ''
  });

  // Entity dropdown options (for selected client + period)
  const [entityOptions, setEntityOptions] = useState([]);
  const [entityLoading, setEntityLoading] = useState(false);

  // Client searchable combobox open state
  const [clientPickerOpen, setClientPickerOpen] = useState(false);

  // In-place regenerate (View Details → uncheck cases → regenerate)
  const [selectedItemCaseIds, setSelectedItemCaseIds] = useState(new Set());
  const [regenLoading, setRegenLoading] = useState(false);

  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'bank_transfer',
    reference_number: '',
    notes: ''
  });

  // Email form
  const [emailForm, setEmailForm] = useState({
    to_email: '',
    cc_email: '',
    subject: '',
    message: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [invoicesRes, clientsRes] = await Promise.all([
        axios.get(`/client-invoices${clientId ? `?client_id=${clientId}` : ''}`),
        !isClientView ? axios.get('/clients') : Promise.resolve({ data: [] })
      ]);
      setInvoices(invoicesRes.data || []);
      setClients(clientsRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [clientId, isClientView]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const stats = useMemo(() => ({
    estimates: invoices.filter(i => i.invoice_type === 'estimate').length,
    invoicesCount: invoices.filter(i => i.invoice_type !== 'estimate').length,
    paid: invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.total_amount || 0), 0),
    outstanding: invoices.filter(i => i.invoice_type !== 'estimate' && i.status !== 'paid')
      .reduce((sum, i) => sum + ((i.balance_due ?? i.total_amount) || 0), 0),
    totalValue: invoices.reduce((sum, i) => sum + (i.total_amount || 0), 0)
  }), [invoices]);

  // Load entities that have billable cases in the selected period for the
  // selected client. Called automatically when client_id + period_from +
  // period_to are all set. Empty dropdown = no cases to bill for this client.
  const loadEntityOptions = async (clientId, pFrom, pTo) => {
    if (!clientId || !pFrom || !pTo) {
      setEntityOptions([]);
      return;
    }
    setEntityLoading(true);
    try {
      const res = await axios.get('/client-invoices/entities-with-cases', {
        params: { client_id: clientId, period_from: pFrom, period_to: pTo }
      });
      setEntityOptions(res.data?.entities || []);
    } catch (err) {
      setEntityOptions([]);
      toast.error('Failed to load entities');
    } finally {
      setEntityLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!generateForm.client_id || !generateForm.entity_id || !generateForm.period_from || !generateForm.period_to) {
      toast.error('Please select client, entity, and period');
      return;
    }
    try {
      // "__ALL__" sentinel = roll up across all entities for this client
      const payload = { ...generateForm, entity_id: generateForm.entity_id === '__ALL__' ? null : generateForm.entity_id };
      const res = await axios.post('/client-invoices/preview', payload);
      setPreviewData(res.data);
      const scope = generateForm.entity_id === '__ALL__' ? 'all entities' : 'this entity';
      toast.success(`Found ${res.data.total_cases} billable cases for ${scope}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No billable cases found');
      setPreviewData(null);
    }
  };

  const handleGenerate = async () => {
    if (!generateForm.client_id || !generateForm.entity_id || !generateForm.period_from || !generateForm.period_to) {
      toast.error('Please select client, entity, and period');
      return;
    }
    try {
      const payload = {
        ...generateForm,
        entity_id: generateForm.entity_id === '__ALL__' ? null : generateForm.entity_id,
        invoice_type: invoiceType
      };
      const res = await axios.post('/client-invoices/generate', payload);
      toast.success(`${invoiceType === 'estimate' ? 'Estimate' : 'Invoice'} ${res.data.id} generated successfully`);
      setShowGenerateDialog(false);
      setPreviewData(null);
      setEntityOptions([]);
      setGenerateForm({ client_id: '', entity_id: '', period_from: '', period_to: '', tax_rate: 18, notes: '', due_date: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate');
    }
  };

  // In-place regenerate the invoice with only the checkbox-selected cases.
  const handleRegenerate = async () => {
    if (!selectedInvoice) return;
    const kept = Array.from(selectedItemCaseIds);
    if (kept.length === 0) {
      toast.error('At least one case must stay on the estimate');
      return;
    }
    const removedCount = (selectedInvoice.items?.length || 0) - kept.length;
    if (removedCount === 0) {
      toast.info('No changes to apply');
      return;
    }
    if (!window.confirm(`Regenerate this draft with ${kept.length} case(s)? ${removedCount} case(s) will be removed and picked up in a future period's estimate.`)) return;
    setRegenLoading(true);
    try {
      const res = await axios.post(`/client-invoices/${selectedInvoice.id}/regenerate`, { keep_case_ids: kept });
      setSelectedInvoice(res.data);
      toast.success(`Regenerated — ${kept.length} case(s), ₹${(res.data.total_amount || 0).toLocaleString()}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to regenerate');
    } finally {
      setRegenLoading(false);
    }
  };

  const handleUpdateStatus = async (invoiceId, newStatus) => {
    try {
      await axios.put(`/client-invoices/${invoiceId}/status`, { status: newStatus });
      toast.success(`Invoice marked as ${newStatus}`);
      fetchData();
      if (selectedInvoice?.id === invoiceId) {
        setSelectedInvoice({ ...selectedInvoice, status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this?')) return;
    try {
      await axios.delete(`/client-invoices/${invoiceId}`);
      toast.success('Deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleRecordPayment = async () => {
    if (!selectedInvoice) return;
    try {
      await axios.post(`/client-invoices/${selectedInvoice.id}/payment`, paymentForm);
      toast.success('Payment recorded successfully');
      setShowPaymentDialog(false);
      setPaymentForm({ amount: 0, payment_date: new Date().toISOString().split('T')[0], payment_method: 'bank_transfer', reference_number: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleConvertToInvoice = async (estimate) => {
    try {
      await axios.post(`/client-invoices/${estimate.id}/convert`);
      toast.success('Estimate converted to invoice');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert');
    }
  };

  const handleSendEmail = async () => {
    if (!selectedInvoice) return;
    if (!emailForm.to_email) {
      toast.error('To Email is required');
      return;
    }
    try {
      // Build the PDF and base64-encode it for SendGrid attachment.
      const doc = buildInvoiceDoc(selectedInvoice);
      const pdfDataUri = doc.output('datauristring'); // "data:application/pdf;filename=...;base64,XXXX"
      const base64Marker = ';base64,';
      const idx = pdfDataUri.indexOf(base64Marker);
      const pdfBase64 = idx >= 0 ? pdfDataUri.substring(idx + base64Marker.length) : '';
      const isEstimate = selectedInvoice.invoice_type === 'estimate';
      const filenameSafe = (selectedInvoice.client_name || 'doc').replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 40);
      const periodSafe = (selectedInvoice.invoice_period || '').replace(/[^a-zA-Z0-9_-]+/g, '_');
      const attachmentFilename = `${isEstimate ? 'Estimate' : 'Invoice'}_${selectedInvoice.id}_${filenameSafe}.pdf`;

      // Build the line-items CSV for the same period and base64-encode it
      const csvText = buildInvoiceItemsCsv(selectedInvoice);
      // btoa needs a binary string; encodeURIComponent + unescape preserves UTF-8
      const csvBase64 = btoa(unescape(encodeURIComponent(csvText)));
      const csvFilename = `${isEstimate ? 'Estimate' : 'Invoice'}_${selectedInvoice.id}_${periodSafe || 'period'}_data.csv`;

      const res = await axios.post(`/client-invoices/${selectedInvoice.id}/send-email`, {
        to_email: emailForm.to_email,
        cc_email: emailForm.cc_email || null,
        subject: emailForm.subject,
        message: emailForm.message,
        pdf_base64: pdfBase64,
        pdf_filename: attachmentFilename,
        csv_base64: csvBase64,
        csv_filename: csvFilename,
      });
      const ccCount = (res.data?.cc || []).length;
      const attachLabel = res.data?.attachments_count ? ` with ${res.data.attachments_count} attachment(s)` : ' with PDF + CSV';
      toast.success(`Email sent to ${res.data.to}${ccCount ? ` (cc: ${ccCount})` : ''}${attachLabel}`);
      setShowEmailDialog(false);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Send failed';
      toast.error(`Failed to send: ${detail}`, { duration: 10000 });
    }
  };

  const handleBulkEstimateReminder = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) { toast.error('Select estimates first'); return; }
    try {
      const res = await axios.post('/client-invoices/bulk-estimate-reminder', { invoice_ids: ids });
      toast.success(`Sent ${res.data.sent} estimate reminders`);
      if (res.data.failed?.length) toast.warning(`${res.data.failed.length} failed`);
      setSelectedIds(new Set());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminders');
    }
  };

  const handleBulkPaymentReminder = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) { toast.error('Select invoices first'); return; }
    try {
      const res = await axios.post('/client-invoices/bulk-payment-reminder', { invoice_ids: ids });
      toast.success(`Sent payment reminders for ${res.data.sent} invoices`);
      if (res.data.failed?.length) toast.warning(`${res.data.failed.length} failed`);
      setSelectedIds(new Set());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminders');
    }
  };

  // ---- Shared CSV builder (single source of truth) ----
  // Both the "Download Data" action and the email attachment call into this.
  // Options:
  //   includeTat=true   → keeps the legacy 'Verification TAT' column (download)
  //   includeTat=false  → drops it (email attachment, per user request)
  // Always prepends a period-summary header (Period | Cases | Total) so
  // finance immediately sees the scope without counting rows.
  const buildInvoiceCsvText = (invoice, { includeTat = true } = {}) => {
    const escape = (v) => {
      if (v == null) return '';
      const s = String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const items = invoice.items || [];
    const totalAmount = Number(invoice.total_amount || 0).toLocaleString('en-IN');

    // Period summary header — single info row, blank separator, then the data table
    const periodSummary = [
      `# ${invoice.invoice_type === 'estimate' ? 'Estimate' : 'Invoice'}: ${invoice.id || ''} | Period: ${invoice.invoice_period || '-'} | Cases: ${items.length} | Total: ₹${totalAmount}`
    ];

    // Build column list dynamically
    const headers = [
      'Sl No', 'Case Received Date', 'Emp/Offer ID', 'Case ID', 'Employee Name',
      'Candidate DOB', 'Father Name',
      'Client Name', 'Entity Name', 'Entity HR', 'Location', 'Checks Conducted',
      'Verification Status', 'Verification Result', 'Date Completed',
      ...(includeTat ? ['Verification TAT'] : []),
      'Rate', 'Additional Cost', 'Total Amount', 'Billed Month'
    ];

    const rows = items.map(item => {
      const base = [
        item.sl_no,
        item.case_received_date,
        item.emp_offer_id,
        item.case_id,
        item.employee_name,
        item.candidate_dob || '',
        item.father_name || '',
        item.client_name,
        item.entity_name,
        item.entity_hr,
        item.location,
        (item.checks_conducted || []).join(', '),
        item.verification_status,
        item.verification_result,
        item.date_completed,
      ];
      if (includeTat) base.push(item.verification_tat);
      base.push(item.rate, item.additional_cost, item.total_amount, item.billed_month);
      return base;
    });

    // Totals footer — same column layout. The "label" cell sits in the
    // 'Rate' column (4 cols from the right: Rate, Additional Cost, Total Amount, Billed Month).
    const blank = new Array(headers.length).fill('');
    const buildFooterRow = (label, valueCol = 'total') => {
      const r = blank.slice();
      r[headers.length - 4] = label;                // 'Rate' position → label
      r[headers.length - 2] = valueCol === 'subtotal' ? invoice.subtotal
                              : valueCol === 'tax'   ? invoice.tax_amount
                              : invoice.total_amount; // 'Total Amount' position → value
      return r;
    };
    const footer = [
      [],
      buildFooterRow('Subtotal:', 'subtotal'),
      buildFooterRow(`Tax (${invoice.tax_rate || 18}%):`, 'tax'),
      buildFooterRow('Total:', 'total'),
    ];

    const allRows = [periodSummary, [], headers, ...rows, ...footer];
    return allRows.map(r => r.map(escape).join(',')).join('\r\n');
  };

  // Download CSV — keeps Verification TAT column (legacy download contract)
  const exportToCSV = (invoice) => {
    const csvContent = buildInvoiceCsvText(invoice, { includeTat: true });
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${invoice.id}_${(invoice.client_name || 'invoice').replace(/\s+/g, '_')}.csv`;
    link.click();
  };

  // Build the invoice/estimate PDF as a jsPDF doc instance (refactored so we
  // can either save it to disk OR pull base64 bytes for email attachment).
  const buildInvoiceDoc = (invoice) => {
    const doc = new jsPDF();
    const docLabel = invoice.invoice_type === 'estimate' ? 'ESTIMATE' : 'INVOICE';
    doc.setFontSize(20);
    doc.setTextColor(66, 66, 66);
    doc.text(docLabel, 20, 25);
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Document #: ${invoice.id}`, 20, 35);
    doc.text(`Date: ${invoice.invoice_date || new Date().toLocaleDateString()}`, 20, 42);
    doc.text(`Period: ${invoice.invoice_period}`, 20, 49);
    doc.setFontSize(12);
    doc.setTextColor(66, 66, 66);
    doc.text('Bill To:', 130, 35);
    doc.setFontSize(10);
    doc.text(invoice.client_name || 'N/A', 130, 42);
    doc.text(invoice.client_address || '', 130, 49);
    const tableData = (invoice.items || []).map((item, idx) => [
      idx + 1,
      item.employee_name || 'N/A',
      item.case_id || '',
      (item.checks_conducted || []).slice(0, 2).join(', '),
      item.verification_result || '-',
      `₹${(item.total_amount || 0).toLocaleString()}`
    ]);
    autoTable(doc, {
      startY: 60,
      head: [['#', 'Employee', 'Case ID', 'Checks', 'Result', 'Amount']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [79, 70, 229], textColor: 255 },
      styles: { fontSize: 8 }
    });
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(10);
    doc.text(`Subtotal: ₹${(invoice.subtotal || 0).toLocaleString()}`, 130, finalY);
    doc.text(`Tax (${invoice.tax_rate || 18}%): ₹${(invoice.tax_amount || 0).toLocaleString()}`, 130, finalY + 7);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(`Total: ₹${(invoice.total_amount || 0).toLocaleString()}`, 130, finalY + 17);
    return doc;
  };

  const generateInvoicePDF = (invoice) => {
    const doc = buildInvoiceDoc(invoice);
    doc.save(`${invoice.id}_${(invoice.client_name || 'invoice').replace(/\s+/g, '_')}.pdf`);
    toast.success('PDF downloaded');
  };

  // Email attachment CSV — same source of truth as the Download action, just
  // with `Verification TAT` excluded per user direction.
  const buildInvoiceItemsCsv = (invoice) => buildInvoiceCsvText(invoice, { includeTat: false });

  const fetchOutstandingReport = () => {
    const outstandingInvoices = invoices.filter(i => i.invoice_type !== 'estimate' && i.status !== 'paid');
    if (outstandingInvoices.length === 0) { toast.info('No outstanding invoices found'); return; }
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Outstanding Invoices Report', 20, 20);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 20, 28);
    const tableData = outstandingInvoices.map(inv => [
      inv.id, inv.client_name,
      `₹${(inv.total_amount || 0).toLocaleString()}`,
      `₹${((inv.balance_due ?? inv.total_amount) || 0).toLocaleString()}`,
      inv.invoice_period, inv.status
    ]);
    autoTable(doc, {
      startY: 35,
      head: [['Invoice #', 'Client', 'Total', 'Balance Due', 'Period', 'Status']],
      body: tableData, theme: 'striped',
      headStyles: { fillColor: [220, 38, 38] }
    });
    const totalOutstanding = outstandingInvoices.reduce((sum, i) => sum + ((i.balance_due ?? i.total_amount) || 0), 0);
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(`Total Outstanding: ₹${totalOutstanding.toLocaleString()}`, 20, finalY);
    doc.save('outstanding_report.pdf');
    toast.success('Outstanding report downloaded');
  };

  const openPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentForm({
      amount: (invoice.balance_due ?? invoice.total_amount) || 0,
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'bank_transfer',
      reference_number: '', notes: ''
    });
    setShowPaymentDialog(true);
  };

  const openEmailDialog = (invoice) => {
    setSelectedInvoice(invoice);
    const client = clients.find(c => c.id === invoice.client_id);
    const isEstimate = invoice.invoice_type === 'estimate';
    const docLabel = isEstimate ? 'Estimate' : 'Invoice';
    // Route to specific Entity HR if this invoice was generated for one entity.
    // For "All Entities" or legacy invoices without entity HR, fall back to
    // client.email (Client Admin HR).
    const recipient = invoice.entity_contact_email || client?.email || invoice.client_email || '';
    const greetingName = invoice.entity_contact_person || invoice.client_name || 'Team';
    setEmailForm({
      to_email: recipient,
      cc_email: '',
      subject: `${docLabel} ${invoice.id} - ${invoice.invoice_period || ''} | Veecheck`.trim(),
      message: `Dear ${greetingName},\n\nPlease find attached ${docLabel.toLowerCase()} ${invoice.id} for the period ${invoice.invoice_period || '-'}.\n\nTotal Amount: ₹${(invoice.total_amount || 0).toLocaleString()}\n\n${isEstimate ? 'Kindly review and approve the estimate at your earliest convenience.' : 'Kindly process the payment at your earliest convenience.'}\n\nThank you for your business.\n\nBest regards,\nVeecheck Team`
    });
    setShowEmailDialog(true);
  };

  // Filtering with date
  const filteredInvoices = useMemo(() => {
    let result = invoices.filter(inv => {
      if (filterClient !== 'all' && inv.client_id !== filterClient) return false;
      if (filterStatus !== 'all' && inv.status !== filterStatus) return false;
      if (filterType !== 'all' && (inv.invoice_type || 'invoice') !== filterType) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!(inv.id?.toLowerCase().includes(q) || inv.client_name?.toLowerCase().includes(q))) return false;
      }
      return true;
    });

    // Apply date filter
    if (dateFilter !== 'all') {
      const presets = getDatePresets();
      let fromDate, toDate;
      if (dateFilter === 'custom') {
        fromDate = customDateFrom;
        toDate = customDateTo;
      } else if (presets[dateFilter]) {
        fromDate = presets[dateFilter].from;
        toDate = presets[dateFilter].to;
      }
      if (fromDate && toDate) {
        result = result.filter(inv => {
          const invDate = inv.invoice_date || inv.created_at?.substring(0, 10);
          return invDate && invDate >= fromDate && invDate <= toDate;
        });
      }
    }

    return result;
  }, [invoices, filterClient, filterStatus, filterType, searchQuery, dateFilter, customDateFrom, customDateTo]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / pageSize));
  const paginatedInvoices = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredInvoices.slice(start, start + pageSize);
  }, [filteredInvoices, currentPage, pageSize]);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [filterClient, filterStatus, filterType, searchQuery, dateFilter, customDateFrom, customDateTo, pageSize]);

  // Bulk selection helpers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === paginatedInvoices.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedInvoices.map(i => i.id)));
    }
  };

  const getPaymentStatusBadge = (invoice) => {
    const status = invoice.payment_status || (invoice.status === 'paid' ? 'paid' : 'unpaid');
    return (
      <Badge className={PAYMENT_STATUS_COLORS[status] || PAYMENT_STATUS_COLORS.unpaid}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="client-invoices">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Client Invoices & Estimates</h2>
          <p className="text-slate-600">
            {isClientView ? 'View your invoices' : 'Generate and manage client invoices'}
          </p>
        </div>
        {!isClientView && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchOutstandingReport} className="text-red-600 hover:text-red-700 border-red-200">
              <Receipt className="w-4 h-4 mr-2" />Outstanding Report
            </Button>
            <Button variant="outline" onClick={() => { setInvoiceType('estimate'); setShowGenerateDialog(true); }} data-testid="generate-estimate-btn">
              <FileCheck2 className="w-4 h-4 mr-2" />Generate Estimate
            </Button>
            <Button onClick={() => { setInvoiceType('invoice'); setShowGenerateDialog(true); }} className="bg-indigo-600 hover:bg-indigo-700" data-testid="generate-invoice-btn">
              <Plus className="w-4 h-4 mr-2" />Generate Invoice
            </Button>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-blue-600">Estimates</p><p className="text-2xl font-bold text-blue-800">{stats.estimates}</p></div>
              <FileCheck2 className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-green-600">Invoices</p><p className="text-2xl font-bold text-green-800">{stats.invoicesCount}</p></div>
              <FileText className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-emerald-600">Paid</p><p className="text-2xl font-bold text-emerald-800">₹{stats.paid.toLocaleString()}</p></div>
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-red-600">Outstanding</p><p className="text-2xl font-bold text-red-800">₹{stats.outstanding.toLocaleString()}</p></div>
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-amber-600">Total Value</p><p className="text-2xl font-bold text-amber-800">₹{stats.totalValue.toLocaleString()}</p></div>
              <DollarSign className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[180px]">
              <Input placeholder="Search by Invoice ID or Client..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="search-input" />
            </div>
            {!isClientView && (
              <Select value={filterClient} onValueChange={setFilterClient}>
                <SelectTrigger className="w-[180px]" data-testid="filter-client"><SelectValue placeholder="All Clients" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map(c => (<SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>))}
                </SelectContent>
              </Select>
            )}
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[140px]" data-testid="filter-type"><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="estimate">Estimates</SelectItem>
                <SelectItem value="invoice">Invoices</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[140px]" data-testid="filter-status"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-[160px]" data-testid="filter-date"><SelectValue placeholder="All Dates" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Dates</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="last_month">Last Month</SelectItem>
                <SelectItem value="last_3_months">Last 3 Months</SelectItem>
                <SelectItem value="custom">Between Dates</SelectItem>
              </SelectContent>
            </Select>
            {dateFilter === 'custom' && (
              <>
                <Input type="date" value={customDateFrom} onChange={(e) => setCustomDateFrom(e.target.value)} className="w-[140px]" data-testid="date-from" />
                <Input type="date" value={customDateTo} onChange={(e) => setCustomDateTo(e.target.value)} className="w-[140px]" data-testid="date-to" />
              </>
            )}
            <Button variant="outline" onClick={fetchData} size="icon"><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <Card className="border-indigo-200 bg-indigo-50">
          <CardContent className="py-3">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-indigo-700">{selectedIds.size} selected</span>
              <Button size="sm" variant="outline" onClick={handleBulkEstimateReminder} className="border-blue-300 text-blue-700 hover:bg-blue-100" data-testid="bulk-estimate-reminder">
                <Bell className="w-4 h-4 mr-1" />Send Estimate Reminder
              </Button>
              <Button size="sm" variant="outline" onClick={handleBulkPaymentReminder} className="border-red-300 text-red-700 hover:bg-red-100" data-testid="bulk-payment-reminder">
                <Mail className="w-4 h-4 mr-1" />Send Payment Reminder
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} className="text-slate-500">Clear</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pagination Controls (Above Table) */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <span>Show</span>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
            <SelectTrigger className="w-[70px] h-8" data-testid="page-size"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <span>of {filteredInvoices.length} entries</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} data-testid="page-first">
            <ChevronsLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} data-testid="page-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="px-3 py-1 text-sm">Page {currentPage} of {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} data-testid="page-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} data-testid="page-last">
            <ChevronsRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Invoices Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading...</div>
          ) : paginatedInvoices.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No invoices found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-10">
                    <Checkbox checked={selectedIds.size === paginatedInvoices.length && paginatedInvoices.length > 0} onCheckedChange={toggleSelectAll} data-testid="select-all" />
                  </TableHead>
                  <TableHead>Document #</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Balance</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedInvoices.map(invoice => (
                  <TableRow 
                    key={invoice.id} 
                    className={
                      invoice.invoice_type === 'estimate' ? 'bg-blue-50/50' : 
                      invoice.status === 'paid' ? 'bg-emerald-50/30' : 
                      invoice.status === 'overdue' ? 'bg-red-50/30' : ''
                    }
                  >
                    <TableCell>
                      <Checkbox checked={selectedIds.has(invoice.id)} onCheckedChange={() => toggleSelect(invoice.id)} data-testid={`select-${invoice.id}`} />
                    </TableCell>
                    <TableCell className="font-mono text-sm font-medium">{invoice.id}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{invoice.client_name}</p>
                        <p className="text-xs text-slate-500">{invoice.billing_type || 'standard'} billing</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={invoice.invoice_type === 'estimate' ? 'border-blue-300 text-blue-700 bg-blue-50' : 'border-green-300 text-green-700 bg-green-50'}>
                        {invoice.invoice_type === 'estimate' ? 'Estimate' : 'Invoice'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{invoice.invoice_period}</TableCell>
                    <TableCell className="font-semibold">₹{(invoice.total_amount || 0).toLocaleString()}</TableCell>
                    <TableCell>
                      {invoice.invoice_type !== 'estimate' ? getPaymentStatusBadge(invoice) : <span className="text-gray-400 text-sm">-</span>}
                    </TableCell>
                    <TableCell>
                      {invoice.invoice_type !== 'estimate' ? (
                        <span className={(invoice.balance_due ?? invoice.total_amount) > 0 ? 'text-red-600 font-semibold' : 'text-emerald-600'}>
                          ₹{((invoice.balance_due ?? invoice.total_amount) || 0).toLocaleString()}
                        </span>
                      ) : <span className="text-gray-400 text-sm">-</span>}
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[invoice.status] || STATUS_COLORS.draft}>{invoice.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => {
                          setSelectedInvoice(invoice);
                          // Pre-check every row so the user "opts out" only items they want to remove
                          setSelectedItemCaseIds(new Set((invoice.items || []).map(it => it.case_id).filter(Boolean)));
                          setShowDetailDialog(true);
                        }} title="View Details" data-testid={`view-${invoice.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => generateInvoicePDF(invoice)} className="text-green-600 hover:text-green-700" title="Download PDF">
                          <Printer className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => exportToCSV(invoice)} className="text-blue-600 hover:text-blue-700" title="Download CSV">
                          <FileSpreadsheet className="w-4 h-4" />
                        </Button>
                        {!isClientView && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => openEmailDialog(invoice)} className="text-purple-600 hover:text-purple-700" title="Send via Email">
                              <Mail className="w-4 h-4" />
                            </Button>
                            {invoice.invoice_type !== 'estimate' && invoice.status !== 'paid' && (
                              <Button variant="ghost" size="sm" onClick={() => openPaymentDialog(invoice)} className="text-emerald-600 hover:text-emerald-700" title="Record Payment">
                                <CreditCard className="w-4 h-4" />
                              </Button>
                            )}
                            {invoice.invoice_type === 'estimate' && invoice.status !== 'converted' && (
                              <Button variant="ghost" size="sm" onClick={() => handleConvertToInvoice(invoice)} className="text-amber-600 hover:text-amber-700" title="Convert to Invoice">
                                <ArrowRight className="w-4 h-4" />
                              </Button>
                            )}
                            {invoice.status === 'draft' && (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => handleUpdateStatus(invoice.id, 'sent')} title="Mark as Sent" className="text-blue-600">
                                  <Send className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(invoice.id)} title="Delete" className="text-red-600">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {invoice.status === 'sent' && invoice.invoice_type !== 'estimate' && (
                              <Button variant="ghost" size="sm" onClick={() => handleUpdateStatus(invoice.id, 'paid')} title="Mark as Paid" className="text-emerald-600">
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Generate Invoice/Estimate Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {invoiceType === 'estimate' ? <FileCheck2 className="w-5 h-5 text-blue-600" /> : <FileText className="w-5 h-5 text-green-600" />}
              Generate {invoiceType === 'estimate' ? 'Estimate' : 'Invoice'}
            </DialogTitle>
            <DialogDescription>Select a client and date range to generate a new {invoiceType}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Client *</Label>
              <Popover open={clientPickerOpen} onOpenChange={setClientPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={clientPickerOpen}
                    className="w-full justify-between font-normal"
                    data-testid="generate-client-combobox-trigger"
                  >
                    {generateForm.client_id ? (
                      <span className="flex items-center gap-2 truncate">
                        <Building2 className="w-4 h-4 shrink-0" />
                        <span className="truncate">
                          {clients.find(c => c.id === generateForm.client_id)?.company_name || 'Select client'}
                        </span>
                      </span>
                    ) : (
                      <span className="text-slate-500">Search and select client…</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Type to search clients (e.g. Moth)…" data-testid="generate-client-combobox-input" />
                    <CommandList>
                      <CommandEmpty>No client found.</CommandEmpty>
                      <CommandGroup>
                        {clients.map(c => (
                          <CommandItem
                            key={c.id}
                            value={`${c.company_name || ''} ${c.email || ''}`}
                            onSelect={() => {
                              setGenerateForm(prev => ({ ...prev, client_id: c.id, entity_id: '' }));
                              setEntityOptions([]);
                              setPreviewData(null);
                              setClientPickerOpen(false);
                              if (generateForm.period_from && generateForm.period_to) {
                                loadEntityOptions(c.id, generateForm.period_from, generateForm.period_to);
                              }
                            }}
                            data-testid={`generate-client-option-${c.id}`}
                          >
                            <Check className={`mr-2 h-4 w-4 ${generateForm.client_id === c.id ? 'opacity-100' : 'opacity-0'}`} />
                            <Building2 className="w-4 h-4 mr-2 shrink-0" />
                            <span className="truncate">{c.company_name}</span>
                            <Badge variant="outline" className="ml-2 text-[10px]">{c.billing_type || 'standard'}</Badge>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Period From *</Label><Input type="date" value={generateForm.period_from} onChange={(e) => {
                const v = e.target.value;
                setGenerateForm({...generateForm, period_from: v, entity_id: ''});
                setPreviewData(null);
                if (generateForm.client_id && generateForm.period_to) loadEntityOptions(generateForm.client_id, v, generateForm.period_to);
              }} /></div>
              <div><Label>Period To *</Label><Input type="date" value={generateForm.period_to} onChange={(e) => {
                const v = e.target.value;
                setGenerateForm({...generateForm, period_to: v, entity_id: ''});
                setPreviewData(null);
                if (generateForm.client_id && generateForm.period_from) loadEntityOptions(generateForm.client_id, generateForm.period_from, v);
              }} /></div>
            </div>
            <div>
              <Label>Entity *</Label>
              <Select value={generateForm.entity_id} onValueChange={(v) => { setGenerateForm({...generateForm, entity_id: v}); setPreviewData(null); }} disabled={entityLoading || entityOptions.length === 0}>
                <SelectTrigger data-testid="generate-estimate-entity-select">
                  <SelectValue placeholder={
                    entityLoading ? 'Loading entities…' :
                    (!generateForm.client_id || !generateForm.period_from || !generateForm.period_to) ? 'Select client and period first' :
                    entityOptions.length === 0 ? 'No billable entities in this period' :
                    'Select entity'
                  } />
                </SelectTrigger>
                <SelectContent>
                  {entityOptions.length > 1 && (
                    <SelectItem value="__ALL__" data-testid="entity-option-all">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        <span className="font-medium">All Entities</span>
                        <Badge variant="outline" className="ml-2 text-xs">
                          {entityOptions.reduce((s, e) => s + (e.case_count || 0), 0)} cases · {entityOptions.length} entities
                        </Badge>
                      </div>
                    </SelectItem>
                  )}
                  {entityOptions.map(ent => (
                    <SelectItem key={ent.entity_id} value={ent.entity_id} data-testid={`entity-option-${ent.entity_id}`}>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        <span>{ent.entity_name}{ent.code ? ` (${ent.code})` : ''}</span>
                        <Badge variant="outline" className="ml-2 text-xs">{ent.case_count} case{ent.case_count === 1 ? '' : 's'}</Badge>
                        {ent.contact_email && <span className="text-[10px] text-slate-500 ml-2 truncate max-w-[140px]">{ent.contact_email}</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">
                Pick a specific entity to bill its HR directly, or choose <strong>All Entities</strong> to roll up into a single estimate for the client. Only entities with billable cases (completed + stop-check with completions) in the selected period are shown.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Tax Rate (%)</Label><Input type="number" value={generateForm.tax_rate} onChange={(e) => setGenerateForm({...generateForm, tax_rate: parseFloat(e.target.value)})} /></div>
              {invoiceType === 'invoice' && (
                <div><Label>Due Date</Label><Input type="date" value={generateForm.due_date} onChange={(e) => setGenerateForm({...generateForm, due_date: e.target.value})} /></div>
              )}
            </div>
            <div><Label>Notes</Label><Textarea placeholder="Optional notes" value={generateForm.notes} onChange={(e) => setGenerateForm({...generateForm, notes: e.target.value})} /></div>
            <Button variant="outline" onClick={handlePreview} className="w-full"><Search className="w-4 h-4 mr-2" />Preview {invoiceType === 'estimate' ? 'Estimate' : 'Invoice'}</Button>
            {previewData && (
              <Card className="bg-slate-50">
                <CardContent className="pt-4">
                  <div className="space-y-2">
                    <p className="font-medium">{previewData.client_name}</p>
                    <p className="text-sm text-slate-600">Billing Type: {previewData.billing_type}</p>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div><p className="text-sm text-slate-500">Total Cases</p><p className="text-xl font-bold">{previewData.total_cases}</p></div>
                      <div><p className="text-sm text-slate-500">Total Amount</p><p className="text-xl font-bold text-emerald-600">₹{previewData.total_amount?.toLocaleString()}</p></div>
                    </div>
                    <div className="text-sm text-slate-600 mt-2">
                      <p>Subtotal: ₹{previewData.subtotal?.toLocaleString()}</p>
                      <p>Tax ({previewData.tax_rate}%): ₹{previewData.tax_amount?.toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowGenerateDialog(false); setPreviewData(null); }}>Cancel</Button>
            <Button onClick={handleGenerate} disabled={!previewData} className={invoiceType === 'estimate' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-indigo-600 hover:bg-indigo-700'}>
              Generate {invoiceType === 'estimate' ? 'Estimate' : 'Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                {selectedInvoice?.invoice_type === 'estimate' ? <FileCheck2 className="w-5 h-5 text-blue-600" /> : <FileText className="w-5 h-5 text-green-600" />}
                {selectedInvoice?.invoice_type === 'estimate' ? 'Estimate' : 'Invoice'} {selectedInvoice?.id}
              </span>
              <Badge className={STATUS_COLORS[selectedInvoice?.status]}>{selectedInvoice?.status}</Badge>
            </DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Client</p>
                  <p className="font-medium">{selectedInvoice.client_name}</p>
                  <p className="text-sm text-slate-600">{selectedInvoice.client_address}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Invoice Period</p>
                  <p className="font-medium">{selectedInvoice.invoice_period}</p>
                  <p className="text-sm text-slate-600">Date: {selectedInvoice.invoice_date}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Billing Type</p>
                  <p className="font-medium capitalize">{selectedInvoice.billing_type}</p>
                  <p className="text-sm text-slate-600">{selectedInvoice.total_cases} cases</p>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {selectedInvoice.status === 'draft' && (
                        <TableHead className="w-10">
                          <input
                            type="checkbox"
                            data-testid="invoice-items-select-all"
                            className="cursor-pointer"
                            checked={selectedInvoice.items && selectedItemCaseIds.size === selectedInvoice.items.length && selectedInvoice.items.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedItemCaseIds(new Set((selectedInvoice.items || []).map(it => it.case_id).filter(Boolean)));
                              } else {
                                setSelectedItemCaseIds(new Set());
                              }
                            }}
                          />
                        </TableHead>
                      )}
                      <TableHead className="w-10">Sl#</TableHead>
                      <TableHead>Received</TableHead>
                      <TableHead>Emp/Offer ID</TableHead>
                      <TableHead>Case ID</TableHead>
                      <TableHead>Employee</TableHead>
                      <TableHead>Entity</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Checks</TableHead>
                      <TableHead>Result</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead>TAT</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Add. Cost</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedInvoice.items?.map((item, idx) => (
                      <TableRow key={idx} className={selectedInvoice.status === 'draft' && !selectedItemCaseIds.has(item.case_id) ? 'opacity-50 line-through' : ''}>
                        {selectedInvoice.status === 'draft' && (
                          <TableCell>
                            <input
                              type="checkbox"
                              data-testid={`invoice-item-checkbox-${item.case_id}`}
                              className="cursor-pointer"
                              checked={selectedItemCaseIds.has(item.case_id)}
                              onChange={(e) => {
                                const next = new Set(selectedItemCaseIds);
                                if (e.target.checked) next.add(item.case_id); else next.delete(item.case_id);
                                setSelectedItemCaseIds(next);
                              }}
                            />
                          </TableCell>
                        )}
                        <TableCell>{item.sl_no}</TableCell>
                        <TableCell className="text-sm">{item.case_received_date}</TableCell>
                        <TableCell className="text-sm font-mono">{item.emp_offer_id}</TableCell>
                        <TableCell className="text-sm font-mono">{item.case_id}</TableCell>
                        <TableCell>{item.employee_name}</TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm">{item.entity_name}</p>
                            {item.entity_hr && <p className="text-xs text-slate-500">{item.entity_hr}</p>}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{item.location}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {item.checks_conducted?.map((check, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{check.replace(/_/g, ' ')}</Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            item.verification_result === 'GREEN' ? 'bg-emerald-100 text-emerald-700' :
                            item.verification_result === 'RED' ? 'bg-red-100 text-red-700' :
                            item.verification_result === 'AMBER' ? 'bg-amber-100 text-amber-700' :
                            item.verification_result === 'Clear' ? 'bg-emerald-100 text-emerald-700' :
                            item.verification_result === 'Discrepancy' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }>{item.verification_result}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">{item.date_completed}</TableCell>
                        <TableCell className="text-sm">{item.verification_tat}d</TableCell>
                        <TableCell className="text-right">₹{item.rate?.toLocaleString()}</TableCell>
                        <TableCell className="text-right">₹{item.additional_cost?.toLocaleString()}</TableCell>
                        <TableCell className="text-right font-medium">₹{item.total_amount?.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              <div className="flex justify-end">
                <div className="w-64 space-y-2 p-4 bg-slate-50 rounded-lg">
                  <div className="flex justify-between"><span className="text-slate-600">Subtotal:</span><span>₹{selectedInvoice.subtotal?.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">Tax ({selectedInvoice.tax_rate}%):</span><span>₹{selectedInvoice.tax_amount?.toLocaleString()}</span></div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2"><span>Total:</span><span className="text-emerald-600">₹{selectedInvoice.total_amount?.toLocaleString()}</span></div>
                  {selectedInvoice.invoice_type !== 'estimate' && selectedInvoice.balance_due !== undefined && (
                    <div className="flex justify-between text-red-600 border-t pt-2"><span>Balance Due:</span><span className="font-semibold">₹{selectedInvoice.balance_due?.toLocaleString()}</span></div>
                  )}
                </div>
              </div>
              
              {selectedInvoice.notes && (
                <div className="p-4 bg-amber-50 rounded-lg">
                  <p className="text-sm font-medium">Notes:</p>
                  <p className="text-sm text-slate-600">{selectedInvoice.notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            {selectedInvoice?.status === 'draft' && selectedInvoice?.items && selectedItemCaseIds.size < selectedInvoice.items.length && (
              <div className="mr-auto text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 self-center">
                {selectedInvoice.items.length - selectedItemCaseIds.size} case(s) will be removed — they'll be picked up in a future period's estimate.
              </div>
            )}
            {selectedInvoice?.status === 'draft' && (
              <Button
                variant="outline"
                data-testid="regenerate-estimate-btn"
                onClick={handleRegenerate}
                disabled={regenLoading || selectedItemCaseIds.size === 0 || (selectedInvoice?.items && selectedItemCaseIds.size === selectedInvoice.items.length)}
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                {regenLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Regenerate with {selectedItemCaseIds.size} case(s)
              </Button>
            )}
            <Button variant="outline" onClick={() => generateInvoicePDF(selectedInvoice)}><Printer className="w-4 h-4 mr-2" />Download PDF</Button>
            <Button variant="outline" onClick={() => exportToCSV(selectedInvoice)}><Download className="w-4 h-4 mr-2" />Export CSV</Button>
            <Button onClick={() => setShowDetailDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-emerald-600" />Record Payment</DialogTitle>
            <DialogDescription>Record payment for Invoice {selectedInvoice?.id}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex justify-between"><span className="text-slate-600">Invoice Total:</span><span className="font-semibold">₹{selectedInvoice?.total_amount?.toLocaleString()}</span></div>
              <div className="flex justify-between text-red-600"><span>Balance Due:</span><span className="font-semibold">₹{(selectedInvoice?.balance_due ?? selectedInvoice?.total_amount)?.toLocaleString()}</span></div>
            </div>
            <div><Label>Payment Amount *</Label><Input type="number" value={paymentForm.amount} onChange={(e) => setPaymentForm({...paymentForm, amount: parseFloat(e.target.value)})} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Payment Date</Label><Input type="date" value={paymentForm.payment_date} onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})} /></div>
              <div>
                <Label>Payment Method</Label>
                <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({...paymentForm, payment_method: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Reference Number</Label><Input placeholder="Transaction/Cheque number" value={paymentForm.reference_number} onChange={(e) => setPaymentForm({...paymentForm, reference_number: e.target.value})} /></div>
            <div><Label>Notes</Label><Textarea placeholder="Optional notes" value={paymentForm.notes} onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
            <Button onClick={handleRecordPayment} className="bg-emerald-600 hover:bg-emerald-700"><Banknote className="w-4 h-4 mr-2" />Record Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-purple-600" />
              Send {selectedInvoice?.invoice_type === 'estimate' ? 'Estimate' : 'Invoice'} via Email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>To Email *</Label><Input type="email" value={emailForm.to_email} onChange={(e) => setEmailForm({...emailForm, to_email: e.target.value})} placeholder="recipient@example.com" /></div>
            <div><Label>CC Email (additional)</Label><Input type="email" value={emailForm.cc_email} onChange={(e) => setEmailForm({...emailForm, cc_email: e.target.value})} placeholder="cc@example.com (comma-separated for multiple)" /><p className="text-[11px] text-slate-500 mt-1">Operations Manager, App Admin, and QC Manager are CC'd by default.</p></div>
            <div><Label>Subject</Label><Input value={emailForm.subject} onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})} /></div>
            <div><Label>Message</Label><Textarea rows={6} value={emailForm.message} onChange={(e) => setEmailForm({...emailForm, message: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Cancel</Button>
            <Button onClick={handleSendEmail} className="bg-purple-600 hover:bg-purple-700"><Send className="w-4 h-4 mr-2" />Send Email</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
